import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import { FiCheck, FiArrowRight, FiExternalLink } from 'react-icons/fi';
import MicrosoftStoreBtn from '../components/MicrosoftStoreBtn';
import ChromeStoreBtn from '../components/ChromeStoreBtn';
import StoreButtons from '../components/StoreButtons';
import { CATEGORIES, byCategory } from '../data/catalog';

// One product per wide row. The rows animate in from a visible resting state — the
// old version parked them at opacity 0 and waited for an observer, which meant a
// screenshot, a print, or a fast scroll caught half the catalogue blank.
const ProductRow = ({ product, n, flip }) => (
  <motion.article
    initial={{ opacity: 0.001, y: 24 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: '-80px' }}
    transition={{ duration: 0.5, ease: [0.22, 0.61, 0.36, 1] }}
    className={`flex flex-col gap-10 lg:gap-16 ${flip ? 'lg:flex-row-reverse' : 'lg:flex-row'}`}
  >
    <div className="flex-1 space-y-6">
      <div className="flex items-center gap-4">
        <span className="font-mono text-xs text-muted tabular">{String(n).padStart(2, '0')}</span>
        <img
          src={product.iconSrc}
          alt=""
          width="44"
          height="44"
          loading="lazy"
          className="h-11 w-11 rounded-md border border-border object-cover"
        />
        <h2 className="font-display text-3xl font-bold text-white">{product.name}</h2>
      </div>

      <p className={`font-mono text-xs uppercase tracking-[0.16em] ${product.color}`}>
        {product.tagline}
      </p>

      <p className="max-w-xl text-lg leading-relaxed text-muted">{product.desc}</p>

      <ul className="grid max-w-xl grid-cols-1 gap-x-8 gap-y-2.5 py-2 sm:grid-cols-2">
        {product.features.map((feature, i) => (
          <li key={i} className="flex items-start gap-2.5 text-sm text-primary">
            <SafeIcon icon={FiCheck} className="mt-1 h-3.5 w-3.5 shrink-0 text-ember" />
            <span>{feature}</span>
          </li>
        ))}
      </ul>

      <dl className="flex flex-wrap gap-x-8 gap-y-2 border-y border-border py-4 font-mono text-xs">
        <div className="flex gap-2">
          <dt className="text-muted">Platform</dt>
          <dd className="text-primary">{product.platform}</dd>
        </div>
        <div className="flex gap-2">
          <dt className="text-muted">Released</dt>
          <dd className="text-primary tabular">{product.year}</dd>
        </div>
        <div className="flex gap-2">
          <dt className="text-muted">Price</dt>
          <dd className="text-primary tabular">{product.priceNote}</dd>
        </div>
      </dl>

      <div className="flex flex-col items-start gap-4 pt-2 sm:flex-row sm:items-center">
        {product.comingSoon && product.stores?.some((s) => s.href) && (
          <StoreButtons stores={product.stores} accentColor="bg-ember hover:bg-ember-bright" />
        )}
        {product.comingSoon && !product.stores?.some((s) => s.href) && (
          <div className="flex flex-wrap items-center gap-3">
            <span className="inline-flex items-center gap-2 rounded-full border border-accent-blue/30 bg-accent-blue/10 px-4 py-2 font-mono text-xs uppercase tracking-[0.16em] text-accent-blue">
              Coming Soon
            </span>
            {product.stores && (
              <span className="font-mono text-xs text-muted">
                {product.stores.map((s) => s.name).join(' · ')}
              </span>
            )}
          </div>
        )}
        {!product.comingSoon && product.storeLink && product.store === 'chrome' && (
          <ChromeStoreBtn accentColor="bg-ember hover:bg-ember-bright" className="w-fit px-7" href={product.storeLink} />
        )}
        {!product.comingSoon && product.storeLink && product.store === 'microsoft' && (
          <MicrosoftStoreBtn accentColor="bg-ember hover:bg-ember-bright" className="w-fit px-7" href={product.storeLink} />
        )}
        {product.link && (
          <Link to={product.link} className="flex items-center gap-2 text-sm font-medium text-muted transition-colors hover:text-white">
            View details <SafeIcon icon={FiArrowRight} />
          </Link>
        )}
        {product.siteLink && (
          <a
            href={product.siteLink}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 text-sm font-medium text-muted transition-colors hover:text-white"
          >
            Product site <SafeIcon icon={FiExternalLink} />
          </a>
        )}
      </div>
    </div>

    <div className="flex-1">
      <div className="overflow-hidden border border-border bg-card">
        <img
          src={product.image}
          alt={`${product.name} screenshot`}
          loading="lazy"
          className="aspect-[16/10] w-full object-cover"
        />
      </div>
    </div>
  </motion.article>
);

const Products = () => {
  // Same rule as the home index: number by the order the rows are rendered.
  const ordered = CATEGORIES.flatMap((cat) => byCategory(cat.id));

  return (
    <div className="pb-24 pt-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <header className="border-b border-border pb-10">
          <p className="label">Catalogue</p>
          <h1 className="mt-5 max-w-3xl font-display text-4xl font-extrabold tracking-tightest text-white md:text-6xl">
            Everything we have shipped.
          </h1>
          <p className="mt-6 max-w-xl text-lg text-muted">
            Games, desktop software, hosted platforms and browser extensions. Four shelves,
            one studio.
          </p>
        </header>

        <nav className="flex flex-wrap gap-6 border-b border-border py-6">
          {CATEGORIES.map((cat) => (
            <a
              key={cat.id}
              href={`#${cat.id}`}
              className="group flex items-baseline gap-2 font-mono text-xs uppercase tracking-[0.16em] text-muted transition-colors hover:text-white"
            >
              {cat.name}
              <span className="tabular text-ember">{String(byCategory(cat.id).length).padStart(2, '0')}</span>
            </a>
          ))}
        </nav>

        <div className="space-y-24">
          {CATEGORIES.map((cat) => {
            const items = byCategory(cat.id);
            if (!items.length) return null;
            return (
              <section key={cat.id} id={cat.id} className="scroll-mt-28 pt-20">
                <div className="mb-14">
                  <p className="rule-label mb-4">{cat.name}</p>
                  <p className="max-w-2xl font-display text-2xl font-semibold text-white">{cat.blurb}</p>
                </div>
                <div className="space-y-24">
                  {items.map((product, index) => (
                    <ProductRow
                      key={product.id}
                      product={product}
                      n={ordered.indexOf(product) + 1}
                      flip={index % 2 === 1}
                    />
                  ))}
                </div>
              </section>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default Products;
